import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { sharesApi } from '../utils/api';
import { formatDate } from '../utils/helpers';
import {
  Link, Clock, Lock, Download, Trash2, AlertCircle, ExternalLink, Copy, Check
} from 'lucide-react';

// Helper to format remaining time
const formatTimeRemaining = (expiresAt) => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry - now;

  if (diff <= 0) return 'Expired';

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
  return `${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
};

const ManageSharesModal = ({ isOpen, onClose, file }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (isOpen && file?.id) {
      loadShares();
    }
  }, [isOpen, file?.id]);

  const loadShares = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sharesApi.getByFileId(file.id);
      // Filter out expired shares
      const activeShares = response.data.filter(
        share => new Date(share.expires_at) > new Date()
      );
      setShares(activeShares);
    } catch (err) {
      console.error('Load shares error:', err);
      setError('Failed to load shares');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (shareId) => {
    if (!window.confirm('Are you sure you want to revoke this share link? Anyone with the link will no longer be able to access the file.')) {
      return;
    }

    try {
      setDeleting(shareId);
      await sharesApi.delete(shareId);
      setShares(shares.filter(s => s.id !== shareId));
    } catch (err) {
      console.error('Delete share error:', err);
      setError('Failed to revoke share');
    } finally {
      setDeleting(null);
    }
  };

  const handleCopy = async (shareId) => {
    try {
      const shareUrl = `${window.location.origin}/share/${shareId}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopiedId(shareId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Copy error:', err);
    }
  };

  const handleClose = () => {
    setShares([]);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Manage shares">
      <div className="space-y-4">
        {/* File info */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Link className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 dark:text-white truncate">
              {file?.original_name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {shares.length} active share{shares.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="spinner"></div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* No shares */}
        {!loading && !error && shares.length === 0 && (
          <div className="text-center py-8">
            <Link className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              No active shares for this file
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Create a share link to share this file with others
            </p>
          </div>
        )}

        {/* Shares list */}
        {!loading && shares.length > 0 && (
          <div className="space-y-3">
            {shares.map((share) => (
              <div
                key={share.id}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    {/* Expiration */}
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-primary-500" />
                      <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                        {formatTimeRemaining(share.expires_at)}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Created: {formatDate(share.created_at)}
                      </span>
                      {share.has_password && (
                        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                          <Lock className="w-3 h-3" />
                          Password protected
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Download className="w-3 h-3" />
                        {share.allow_download ? 'Download allowed' : 'No download'}
                      </span>
                    </div>

                    {/* Expiry date */}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                      Expires: {formatDate(share.expires_at)}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleCopy(share.id)}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Copy link"
                    >
                      {copiedId === share.id ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    <a
                      href={`/share/${share.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Open link"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => handleDelete(share.id)}
                      disabled={deleting === share.id}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                      title="Revoke share"
                    >
                      {deleting === share.id ? (
                        <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Close button */}
        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={handleClose} className="btn btn-primary">
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ManageSharesModal;
